import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Globe, Plus, Users, BarChart3, MapPin, Building2, Loader2,
  Settings, CheckCircle2, TrendingUp, Target, FileText, XCircle, ChevronRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PROGRAMS = [
  { value: 'purpose_driven', label: 'Purpose Driven', color: 'bg-teal-500/20 text-teal-300 border-teal-500/30' },
  { value: 'our_square', label: 'Our Square', color: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
  { value: 'heat', label: 'HEAT', color: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
  { value: 'her', label: 'HER', color: 'bg-pink-500/20 text-pink-300 border-pink-500/30' },
];

function ProgramBadge({ program }) {
  const p = PROGRAMS.find(pr => pr.value === program);
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${p?.color || 'bg-slate-500/20 text-slate-300 border-slate-500/30'}`}>{p?.label || program}</span>;
}

function RegionCard({ region, onClick }) {
  const brand = region.brand || {};
  return (
    <Card
      className="bg-slate-800/60 border-slate-700/50 hover:border-blue-500/40 cursor-pointer transition-all"
      onClick={onClick}
      data-testid={`region-card-${region.code}`}
    >
      <CardContent className="pt-5 pb-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: brand.primary_color || '#00A4B4' }}>
              {region.code?.slice(0, 2).toUpperCase()}
            </div>
            <div>
              <h3 className="text-white font-semibold">{brand.name || region.name}</h3>
              <p className="text-xs text-slate-400 flex items-center gap-1">
                <MapPin className="h-3 w-3" /> {region.city}{region.state ? `, ${region.state}` : ''}
              </p>
            </div>
          </div>
          <ChevronRight className="h-4 w-4 text-slate-500" />
        </div>
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="text-center bg-slate-900/50 rounded-lg p-2">
            <p className="text-lg font-bold text-blue-400">{region.total_clients || 0}</p>
            <p className="text-[10px] text-slate-500">Clients</p>
          </div>
          <div className="text-center bg-slate-900/50 rounded-lg p-2">
            <p className="text-lg font-bold text-emerald-400">{region.total_staff || 0}</p>
            <p className="text-[10px] text-slate-500">Staff</p>
          </div>
          <div className="text-center bg-slate-900/50 rounded-lg p-2">
            <p className="text-lg font-bold text-purple-400">{region.assessments_completed || 0}</p>
            <p className="text-[10px] text-slate-500">Assessments</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-1">
          {(region.programs || []).map(p => <ProgramBadge key={p} program={p} />)}
        </div>
      </CardContent>
    </Card>
  );
}

export default function RegionsPage() {
  const { user, token } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [regionDetail, setRegionDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ programs: ['purpose_driven'] });

  const headers = { 'Content-Type': 'application/json' };
  const isAdmin = ['admin', 'network_admin'].includes(user?.role);

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    setLoading(true);
    try {
      const [analRes, regRes] = await Promise.all([
        fetch(`${API}/api/regions/analytics/network`, { headers, credentials: 'include' }),
        fetch(`${API}/api/regions`, { headers, credentials: 'include' }),
      ]);
      if (analRes.ok) {
        const d = await analRes.json();
        setAnalytics(d.network_summary);
        setRegions(d.regions || []);
      } else if (regRes.ok) {
        const d = await regRes.json();
        setRegions(d.regions || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadRegionDetail = async (regionId) => {
    try {
      const res = await fetch(`${API}/api/regions/${regionId}/analytics`, { headers, credentials: 'include' });
      if (res.ok) setRegionDetail(await res.json());
    } catch (e) { console.error(e); }
  };

  const createRegion = async () => {
    if (!form.name || !form.code) return;
    try {
      const body = {
        name: form.name,
        code: form.code,
        city: form.city || '',
        state: form.state || '',
        county: form.county || '',
        brand_name: form.brand_name || form.name,
        brand_tagline: form.brand_tagline || '',
        brand_primary_color: form.brand_primary_color || '#00A4B4',
        brand_secondary_color: form.brand_secondary_color || '#80D153',
        programs: form.programs || ['purpose_driven'],
        contact_email: form.contact_email || '',
        contact_phone: form.contact_phone || '',
      };
      const res = await fetch(`${API}/api/regions`, { method: 'POST', headers, credentials: 'include', body: JSON.stringify(body) });
      if (res.ok) {
        setShowCreate(false);
        setForm({ programs: ['purpose_driven'] });
        loadData();
      }
    } catch (e) { console.error(e); }
  };

  const toggleProgram = (prog) => {
    setForm(p => {
      const current = p.programs || [];
      return { ...p, programs: current.includes(prog) ? current.filter(x => x !== prog) : [...current, prog] };
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-blue-400" /></div>;
  }

  // Region Detail View
  if (selectedRegion && regionDetail) {
    const rd = regionDetail;
    const eng = rd.engagement || {};
    const risk = rd.average_risk_scores || {};
    return (
      <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
        <Button variant="ghost" className="text-slate-400" onClick={() => { setSelectedRegion(null); setRegionDetail(null); }}>
          Back to Network
        </Button>
        <div className="flex items-center gap-3">
          <Globe className="h-8 w-8 text-blue-400" />
          <div>
            <h1 className="text-2xl font-bold text-white" data-testid="region-name">{rd.region?.name}</h1>
            <p className="text-slate-400 text-sm">Region Code: {rd.region?.code}</p>
          </div>
        </div>

        {/* Users by Role */}
        <Card className="bg-slate-800/60 border-slate-700/50">
          <CardHeader><CardTitle className="text-white text-base flex items-center gap-2"><Users className="h-4 w-4 text-blue-400" /> Users ({rd.total_users})</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Object.entries(rd.users_by_role || {}).map(([role, count]) => (
                <div key={role} className="bg-slate-900/50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-white">{count}</p>
                  <p className="text-xs text-slate-400">{role.replace('_', ' ')}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Engagement */}
        <Card className="bg-slate-800/60 border-slate-700/50">
          <CardHeader><CardTitle className="text-white text-base flex items-center gap-2"><TrendingUp className="h-4 w-4 text-emerald-400" /> Engagement</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-blue-400">{eng.total_checkins}</p>
                <p className="text-xs text-slate-400">Check-ins</p>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-emerald-400">{eng.goals_completed}</p>
                <p className="text-xs text-slate-400">Goals Completed</p>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-purple-400">{eng.assessments_completed}</p>
                <p className="text-xs text-slate-400">Assessments</p>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-yellow-400">{eng.goal_completion_rate}%</p>
                <p className="text-xs text-slate-400">Goal Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Risk Averages */}
        {Object.keys(risk).length > 0 && (
          <Card className="bg-slate-800/60 border-slate-700/50">
            <CardHeader><CardTitle className="text-white text-base">Average Risk Scores</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {Object.entries(risk).map(([cat, avg]) => {
                  const color = avg <= 3 ? 'text-emerald-400' : avg <= 5 ? 'text-yellow-400' : avg <= 7 ? 'text-orange-400' : 'text-red-400';
                  return (
                    <div key={cat} className="bg-slate-900/50 rounded-lg p-3 text-center">
                      <p className={`text-lg font-bold ${color}`}>{avg}/10</p>
                      <p className="text-xs text-slate-400">{cat}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Network Overview
  const ns = analytics || {};
  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6" data-testid="regions-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Globe className="h-8 w-8 text-blue-400" />
          <div>
            <h1 className="text-2xl font-bold text-white" data-testid="network-title">Network Overview</h1>
            <p className="text-slate-400 text-sm">Multi-region franchise management & analytics</p>
          </div>
        </div>
        {isAdmin && (
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setShowCreate(true)} data-testid="new-region-btn">
            <Plus className="h-4 w-4 mr-2" /> New Region
          </Button>
        )}
      </div>

      {/* Network-level Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          { label: 'Regions', value: ns.total_regions || regions.length, color: 'text-blue-400', icon: Globe },
          { label: 'Total Users', value: ns.total_users || 0, color: 'text-white', icon: Users },
          { label: 'Clients', value: ns.total_clients || 0, color: 'text-emerald-400', icon: Users },
          { label: 'Staff', value: ns.total_staff || 0, color: 'text-purple-400', icon: Building2 },
          { label: 'Check-ins', value: ns.total_checkins || 0, color: 'text-yellow-400', icon: CheckCircle2 },
          { label: 'Goals Done', value: ns.total_goals_completed || 0, color: 'text-orange-400', icon: Target },
          { label: 'Assessments', value: ns.total_assessments || 0, color: 'text-pink-400', icon: FileText },
        ].map(s => (
          <div key={s.label} className="rounded-xl border border-slate-700/50 bg-slate-800/60 p-3 text-center" data-testid={`net-stat-${s.label.toLowerCase().replace(' ', '-')}`}>
            <s.icon className={`h-4 w-4 mx-auto mb-1 ${s.color}`} />
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-[10px] text-slate-400">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Create Region Form */}
      {showCreate && (
        <Card className="bg-slate-800/60 border-blue-500/30">
          <CardHeader><CardTitle className="text-white text-base">Create New Region / Franchise</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input type="text" value={form.name || ''} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Region Name (e.g. Purpose Driven Dallas)" className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white text-sm" />
              <input type="text" value={form.code || ''} onChange={e => setForm(p => ({ ...p, code: e.target.value }))} placeholder="Code (e.g. pd-dallas)" className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white text-sm" />
              <input type="text" value={form.city || ''} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} placeholder="City" className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white text-sm" />
              <input type="text" value={form.state || ''} onChange={e => setForm(p => ({ ...p, state: e.target.value }))} placeholder="State" className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input type="text" value={form.brand_name || ''} onChange={e => setForm(p => ({ ...p, brand_name: e.target.value }))} placeholder="Display Name (white-label)" className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white text-sm" />
              <input type="text" value={form.brand_tagline || ''} onChange={e => setForm(p => ({ ...p, brand_tagline: e.target.value }))} placeholder="Tagline" className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white text-sm" />
              <div className="flex items-center gap-2">
                <label className="text-xs text-slate-400">Primary</label>
                <input type="color" value={form.brand_primary_color || '#00A4B4'} onChange={e => setForm(p => ({ ...p, brand_primary_color: e.target.value }))} className="w-8 h-8 rounded cursor-pointer bg-transparent border-0" />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-slate-400">Secondary</label>
                <input type="color" value={form.brand_secondary_color || '#80D153'} onChange={e => setForm(p => ({ ...p, brand_secondary_color: e.target.value }))} className="w-8 h-8 rounded cursor-pointer bg-transparent border-0" />
              </div>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-2">Programs</p>
              <div className="flex flex-wrap gap-2">
                {PROGRAMS.map(p => (
                  <button
                    key={p.value}
                    onClick={() => toggleProgram(p.value)}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-all ${(form.programs || []).includes(p.value) ? p.color + ' opacity-100' : 'bg-slate-900 text-slate-500 border-slate-700 opacity-60'}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input type="email" value={form.contact_email || ''} onChange={e => setForm(p => ({ ...p, contact_email: e.target.value }))} placeholder="Contact Email" className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white text-sm" />
              <input type="tel" value={form.contact_phone || ''} onChange={e => setForm(p => ({ ...p, contact_phone: e.target.value }))} placeholder="Contact Phone" className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white text-sm" />
            </div>
            <div className="flex gap-2">
              <Button className="bg-blue-600" onClick={createRegion} data-testid="create-region-submit">Create Region</Button>
              <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Region Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {regions.map(r => (
          <RegionCard key={r.id} region={r} onClick={() => { setSelectedRegion(r); loadRegionDetail(r.id); }} />
        ))}
        {regions.length === 0 && (
          <div className="col-span-2 text-center py-12">
            <Globe className="h-12 w-12 mx-auto mb-4 text-slate-500" />
            <p className="text-slate-400">No regions created yet. Click "New Region" to set up your first franchise.</p>
          </div>
        )}
      </div>
    </div>
  );
}
